import { z } from "zod";

import {
  SupportAssigneeListSchema,
  SupportMessageInputSchema,
  SupportMessageResultSchema,
  SupportTicketListSchema,
  SupportTicketResponseSchema,
  SupportTicketUpdateSchema,
  SupportToolListSchema,
  SupportUnmatchedAcceptResultSchema,
  SupportUploadResultSchema,
  SupportUnmatchedAcceptSchema,
  SupportUnmatchedListSchema,
  SupportUpdateResultSchema,
  SupportViewCountsSchema,
  type SupportMessageInput,
  type SupportTicketUpdate,
  type SupportViewKey,
} from "../../shared/support-schemas";
import {
  SupportAutomationOverviewSchema,
  SupportSettingsUpdateSchema,
  type SupportSettingsUpdate,
} from "../../shared/support-automation";
import {
  SupportBulkUpdateResultSchema,
  SupportBulkUpdateSchema,
  SupportSavedReplyInputSchema,
  SupportSavedReplyListSchema,
  SupportSavedReplyResultSchema,
  type SupportSavedReplyInput,
} from "../../shared/support-saved-replies";
import { parseResponse } from "../api/client-core";

// ADR-0089: the operations console's support desk client. The Operations MCP calls the same
// server routes' services, so what an employee does here an AI client can do through MCP.

const root = "/api/ops/support";
const read: RequestInit = { credentials: "same-origin" };
const send = (method: string, body?: unknown): RequestInit => ({
  method,
  credentials: "same-origin",
  ...(body === undefined
    ? {}
    : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
});
const ticketPath = (ticketId: string) => `${root}/tickets/${encodeURIComponent(ticketId)}`;

/** Where the console downloads one attachment. The route answers with the file, not JSON. */
export const supportAttachmentUrl = (ticketId: string, attachmentId: string) =>
  `${ticketPath(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`;

export async function getSupportViews() {
  return parseResponse(await fetch(`${root}/views`, read), SupportViewCountsSchema);
}

export async function listSupportTickets(input: {
  view: SupportViewKey;
  search?: string;
  cursor?: string | null;
  organizationId?: string;
  limit?: number;
}) {
  const query = new URLSearchParams({ view: input.view });
  if (input.search?.trim()) query.set("search", input.search.trim());
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.organizationId) query.set("organizationId", input.organizationId);
  if (input.limit !== undefined) query.set("limit", String(input.limit));
  return parseResponse(
    await fetch(`${root}/tickets?${query.toString()}`, read),
    SupportTicketListSchema,
  );
}

export async function getSupportTicket(ticketId: string) {
  return parseResponse(await fetch(ticketPath(ticketId), read), SupportTicketResponseSchema);
}

export async function updateSupportTicket(ticketId: string, update: SupportTicketUpdate) {
  return parseResponse(
    await fetch(ticketPath(ticketId), send("PATCH", SupportTicketUpdateSchema.parse(update))),
    SupportUpdateResultSchema,
  );
}

export async function addSupportMessage(ticketId: string, input: SupportMessageInput) {
  return parseResponse(
    await fetch(
      `${ticketPath(ticketId)}/messages`,
      send("POST", SupportMessageInputSchema.parse(input)),
    ),
    SupportMessageResultSchema,
  );
}

/** Uploads one file for a reply or note; the message names it by the returned ID. */
export async function uploadSupportFile(ticketId: string, file: File) {
  const name = encodeURIComponent(file.name.trim() || "pasted-image.png");
  return parseResponse(
    await fetch(`${ticketPath(ticketId)}/uploads?name=${name}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    }),
    SupportUploadResultSchema,
  );
}

export async function discardSupportDraft(ticketId: string) {
  return parseResponse(
    await fetch(`${ticketPath(ticketId)}/draft`, send("DELETE")),
    SupportTicketResponseSchema,
  );
}

export async function listSupportAssignees(organizationId: string) {
  return parseResponse(
    await fetch(`${root}/customers/${encodeURIComponent(organizationId)}/assignees`, read),
    SupportAssigneeListSchema,
  );
}

/** The customer's tools a ticket can be linked to. */
export async function listSupportTools(organizationId: string) {
  return parseResponse(
    await fetch(`${root}/customers/${encodeURIComponent(organizationId)}/tools`, read),
    SupportToolListSchema,
  );
}

export async function listUnmatchedMail() {
  return parseResponse(await fetch(`${root}/unmatched`, read), SupportUnmatchedListSchema);
}

export async function acceptUnmatchedMail(
  mailId: string,
  input: { organizationId: string; requesterName?: string },
) {
  return parseResponse(
    await fetch(
      `${root}/unmatched/${encodeURIComponent(mailId)}/accept`,
      send("POST", SupportUnmatchedAcceptSchema.parse(input)),
    ),
    SupportUnmatchedAcceptResultSchema,
  );
}

export async function rejectUnmatchedMail(mailId: string) {
  return parseResponse(
    await fetch(`${root}/unmatched/${encodeURIComponent(mailId)}/reject`, send("POST")),
    z.strictObject({ rejected: z.literal(true) }),
  );
}

/** The AI settings and the newest automation runs (ADR-0089). */
export async function getSupportSettings() {
  return parseResponse(await fetch(`${root}/settings`, read), SupportAutomationOverviewSchema);
}

export async function updateSupportSettings(update: SupportSettingsUpdate) {
  return parseResponse(
    await fetch(`${root}/settings`, send("PUT", SupportSettingsUpdateSchema.parse(update))),
    SupportAutomationOverviewSchema,
  );
}

/** One change to many tickets; each is changed or refused on its own. */
export async function bulkUpdateSupportTickets(
  ticketIds: readonly string[],
  update: SupportTicketUpdate,
) {
  return parseResponse(
    await fetch(
      `${root}/tickets`,
      send("PATCH", SupportBulkUpdateSchema.parse({ ticketIds, update })),
    ),
    SupportBulkUpdateResultSchema,
  );
}

export async function listSavedReplies() {
  return parseResponse(await fetch(`${root}/saved-replies`, read), SupportSavedReplyListSchema);
}

export async function saveSavedReply(replyId: string | null, input: SupportSavedReplyInput) {
  const body = SupportSavedReplyInputSchema.parse(input);
  const path =
    replyId === null
      ? `${root}/saved-replies`
      : `${root}/saved-replies/${encodeURIComponent(replyId)}`;
  return parseResponse(
    await fetch(path, send(replyId === null ? "POST" : "PUT", body)),
    SupportSavedReplyResultSchema,
  );
}

export async function deleteSavedReply(replyId: string) {
  return parseResponse(
    await fetch(`${root}/saved-replies/${encodeURIComponent(replyId)}`, send("DELETE")),
    z.strictObject({ deleted: z.literal(true) }),
  );
}

export const supportKeys = {
  all: ["ops", "support"] as const,
  views: () => ["ops", "support", "views"] as const,
  list: (view: SupportViewKey, search: string, organizationId?: string) =>
    ["ops", "support", "list", view, search, organizationId ?? null] as const,
  ticket: (ticketId: string) => ["ops", "support", "ticket", ticketId] as const,
  assignees: (organizationId: string) => ["ops", "support", "assignees", organizationId] as const,
  unmatched: () => ["ops", "support", "unmatched"] as const,
  settings: () => ["ops", "support", "settings"] as const,
  savedReplies: () => ["ops", "support", "saved-replies"] as const,
  tools: (organizationId: string) => ["ops", "support", "tools", organizationId] as const,
};

/**
 * The desk keeps itself current without a reload: an open ticket every 10 seconds, so a customer's
 * email or an AI draft appears while someone is on it, and queues every 30 seconds. Both also
 * refresh when the window regains focus.
 */
export const LIVE_TICKET = { refetchInterval: 10_000, refetchOnWindowFocus: true } as const;
export const LIVE_LIST = { refetchInterval: 30_000, refetchOnWindowFocus: true } as const;
